import React, {
    Suspense,
    useEffect,
    useMemo,
    useState,
} from 'react';
import PropTypes from 'prop-types';

import {
    FALLBACK_ATTRIBUTION,
    FALLBACK_CENTER,
    FALLBACK_ZOOM,
    sanitizeAttribution,
    sanitizeBoolean,
    sanitizeCenter,
    sanitizeMarkers,
    sanitizeTileUrl,
    sanitizeZoom,
} from '../../../utils/mapConfig';
import './OpenStreetMapView.scss';

const DEFAULT_ARIA_LABEL = 'Discover map showing nearby musicians';
const coordinateInputPropType = PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
]);
const EMPTY_DRAGGABLE_MARKER_IDS = [];

const envCenter = sanitizeCenter({
    lat: process.env.REACT_APP_MAP_DEFAULT_LAT,
    lng: process.env.REACT_APP_MAP_DEFAULT_LNG,
}, FALLBACK_CENTER);
const MapViewInner = React.lazy(() => import('./OpenStreetMapViewInner'));

const envZoom = sanitizeZoom(process.env.REACT_APP_MAP_DEFAULT_ZOOM, FALLBACK_ZOOM);
const envAttribution = sanitizeAttribution(process.env.REACT_APP_MAP_ATTRIBUTION, FALLBACK_ATTRIBUTION);
const envTileUrl = sanitizeTileUrl(process.env.REACT_APP_MAP_TILE_URL);
const shouldDeferUntilIdle = sanitizeBoolean(process.env.REACT_APP_MAP_PREFETCH_ON_IDLE, true);

function buildClassName(base, modifier, extra) {
    const classes = [base];
    if (modifier) {
        classes.push(`${base}--${modifier}`);
    }

    if (extra) {
        classes.push(extra);
    }

    return classes.join(' ');
}

export default function OpenStreetMapView({
    center = undefined,
    zoom = undefined,
    minZoom = undefined,
    tileUrl = undefined,
    attribution = undefined,
    markers = undefined,
    markerDataKey = undefined,
    viewportKey = undefined,
    preserveOpenPopupOnDataGap = false,
    ariaLabel = DEFAULT_ARIA_LABEL,
    className = '',
    containerRef,
    loadingLabel = 'Loading map experience…',
    overlay = null,
    children = null,
    onMapClick = undefined,
    currentUserId = undefined,
    currentUsername = undefined,
    onEditMarker = undefined,
    onOpenMarkerDetails = undefined,
    markerColors = undefined,
    selectionMode = 'none',
    onSelectionChange = undefined,
    selectedMarkerIds = undefined,
    onViewportChange = undefined,
    cameraRequest = null,
    searchResultMarker = null,
    draggableMarkerIds = EMPTY_DRAGGABLE_MARKER_IDS,
    onMarkerDragEnd = undefined,
    disableMarkerPopups = false,
}) {
    const resolvedTileUrl = tileUrl ? sanitizeTileUrl(tileUrl) : envTileUrl;
    const resolvedCenter = sanitizeCenter(center, envCenter);
    const resolvedZoom = sanitizeZoom(zoom, envZoom);
    const resolvedMinZoom = useMemo(() => {
        if (minZoom === undefined || minZoom === null || minZoom === '') {
            return undefined;
        }
        return sanitizeZoom(minZoom, 0);
    }, [minZoom]);
    const clampedZoom = Number.isFinite(resolvedMinZoom)
        ? Math.max(resolvedZoom, resolvedMinZoom)
        : resolvedZoom;
    const resolvedAttribution = sanitizeAttribution(attribution, envAttribution);
    const sanitizedMarkers = useMemo(
        () => sanitizeMarkers(markers),
        [markers],
    );
    const [isIdleReady, setIsIdleReady] = useState(!shouldDeferUntilIdle);

    useEffect(() => {
        if (!shouldDeferUntilIdle) {
            return undefined;
        }

        let isMounted = true;
        let idleHandle = null;
        let timeoutHandle = null;

        const markReady = () => {
            if (isMounted) {
                setIsIdleReady(true);
            }
        };

        if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
            idleHandle = window.requestIdleCallback(markReady, { timeout: 750 });
        } else {
            timeoutHandle = window.setTimeout(markReady, 0);
        }

        return () => {
            isMounted = false;
            if (idleHandle != null && typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function') {
                window.cancelIdleCallback(idleHandle);
            }
            if (timeoutHandle != null && typeof window !== 'undefined') {
                window.clearTimeout(timeoutHandle);
            }
        };
    }, []);

    if (!resolvedTileUrl) {
        return (
            <div
                className={buildClassName('openstreet-map-view', 'fallback', className)}
                role="status"
            >
                <p className="openstreet-map-view__message">
                    Map tiles are unavailable. Configure <code>REACT_APP_MAP_TILE_URL</code> to enable the Discover map.
                </p>
            </div>
        );
    }

    return (
        <div
            className={buildClassName('openstreet-map-view', null, className)}
            role="region"
            aria-label={ariaLabel}
            ref={containerRef}
        >
            {overlay && (
                <div className="openstreet-map-view__overlay" aria-live="polite">
                    {overlay}
                </div>
            )}
            {!isIdleReady && (
                <div className="openstreet-map-view__loading" role="status" aria-live="polite">
                    {loadingLabel}
                </div>
            )}
            {isIdleReady && (
            <Suspense
                fallback={(
                    <div className="openstreet-map-view__loading" role="status" aria-live="polite">
                        {loadingLabel}
                    </div>
                )}
            >
                <MapViewInner
                    center={resolvedCenter}
                    zoom={clampedZoom}
                    minZoom={resolvedMinZoom}
                    tileUrl={resolvedTileUrl}
                    attribution={resolvedAttribution}
                    markers={sanitizedMarkers}
                    markerDataKey={markerDataKey}
                    viewportKey={viewportKey}
                    preserveOpenPopupOnDataGap={preserveOpenPopupOnDataGap}
                    onMapClick={onMapClick}
                    currentUserId={currentUserId}
                    currentUsername={currentUsername}
                    onEditMarker={onEditMarker}
                    onOpenMarkerDetails={onOpenMarkerDetails}
                    markerColors={markerColors}
                    selectionMode={selectionMode}
                    onSelectionChange={onSelectionChange}
                    selectedMarkerIds={selectedMarkerIds}
                    onViewportChange={onViewportChange}
                    cameraRequest={cameraRequest}
                    searchResultMarker={searchResultMarker}
                    draggableMarkerIds={draggableMarkerIds}
                    onMarkerDragEnd={onMarkerDragEnd}
                    disableMarkerPopups={disableMarkerPopups}
                >
                    {children}
                </MapViewInner>
            </Suspense>
            )}
        </div>
    );
}

OpenStreetMapView.propTypes = {
    center: PropTypes.shape({
        lat: coordinateInputPropType,
        lng: coordinateInputPropType,
        latitude: coordinateInputPropType,
        longitude: coordinateInputPropType,
        lon: coordinateInputPropType,
    }),
    zoom: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    minZoom: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    tileUrl: PropTypes.string,
    attribution: PropTypes.string,
    markers: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        type: PropTypes.string,
        coordinate: PropTypes.shape({
            lat: coordinateInputPropType,
            lng: coordinateInputPropType,
            latitude: coordinateInputPropType,
            longitude: coordinateInputPropType,
            lon: coordinateInputPropType,
        }).isRequired,
        metadata: PropTypes.object,
    })),
    markerDataKey: PropTypes.string,
    viewportKey: PropTypes.string,
    preserveOpenPopupOnDataGap: PropTypes.bool,
    ariaLabel: PropTypes.string,
    className: PropTypes.string,
    containerRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    ]),
    loadingLabel: PropTypes.string,
    overlay: PropTypes.node,
    children: PropTypes.node,
    onMapClick: PropTypes.func,
    currentUserId: PropTypes.string,
    currentUsername: PropTypes.string,
    onEditMarker: PropTypes.func,
    onOpenMarkerDetails: PropTypes.func,
    markerColors: PropTypes.objectOf(PropTypes.string),
    selectionMode: PropTypes.string,
    onSelectionChange: PropTypes.func,
    selectedMarkerIds: PropTypes.arrayOf(PropTypes.string),
    onViewportChange: PropTypes.func,
    cameraRequest: PropTypes.shape({
        id: PropTypes.string.isRequired,
        center: PropTypes.shape({
            lat: PropTypes.number.isRequired,
            lng: PropTypes.number.isRequired,
        }).isRequired,
        zoom: PropTypes.number,
    }),
    searchResultMarker: PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string,
        coordinate: PropTypes.shape({
            lat: PropTypes.number.isRequired,
            lng: PropTypes.number.isRequired,
        }).isRequired,
        color: PropTypes.string,
    }),
    draggableMarkerIds: PropTypes.arrayOf(PropTypes.string),
    onMarkerDragEnd: PropTypes.func,
    disableMarkerPopups: PropTypes.bool,
};
